import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AISEOSection({ user }) {
  const [activeTab, setActiveTab] = useState('website');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Website SEO State
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [seoReport, setSeoReport] = useState(null);
  
  // Meta Generator State
  const [metaInput, setMetaInput] = useState({
    pageTitle: '',
    pageDescription: '',
    keywords: '',
    targetAudience: ''
  });
  const [generatedMeta, setGeneratedMeta] = useState(null);
  
  // Content SEO State
  const [contentText, setContentText] = useState('');
  const [contentAnalysis, setContentAnalysis] = useState(null);
  
  // Hashtag Generator State
  const [hashtagTopic, setHashtagTopic] = useState('');
  const [generatedHashtags, setGeneratedHashtags] = useState(null);
  
  // Blog Writer State
  const [blogTopic, setBlogTopic] = useState('');
  const [blogKeywords, setBlogKeywords] = useState('');
  const [generatedBlog, setGeneratedBlog] = useState(null);

  const tabs = [
    { id: 'website', label: 'Website Analyzer', icon: '🌐' },
    { id: 'meta', label: 'Meta Generator', icon: '🏷️' },
    { id: 'content', label: 'Content SEO', icon: '📝' },
    { id: 'hashtags', label: 'Smart Hashtags', icon: '#️⃣' },
    { id: 'blog', label: 'Blog Writer', icon: '📰' }
  ];

  // Analyze Website SEO
  const analyzeWebsite = async () => {
    if (!websiteUrl) return;
    setLoading(true);
    setError('');
    setSeoReport(null);
    
    try {
      const response = await axios.post(`${API}/seo/analyze-website`, {
        url: websiteUrl,
        user_id: user.id
      });
      setSeoReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to analyze website');
    } finally {
      setLoading(false);
    }
  };

  // Generate Meta Tags
  const generateMeta = async () => {
    if (!metaInput.pageTitle) return;
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/seo/generate-meta`, {
        ...metaInput,
        user_id: user.id,
        industry: user.industry
      });
      setGeneratedMeta(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate meta tags');
    } finally {
      setLoading(false);
    }
  };

  // Analyze Content SEO
  const analyzeContent = async () => {
    if (!contentText) return;
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/seo/analyze-content`, {
        content: contentText,
        user_id: user.id,
        industry: user.industry
      });
      setContentAnalysis(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to analyze content');
    } finally {
      setLoading(false);
    }
  };

  // Generate Hashtags
  const generateHashtags = async () => {
    if (!hashtagTopic) return;
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/seo/generate-hashtags`, {
        topic: hashtagTopic,
        user_id: user.id,
        industry: user.industry
      });
      setGeneratedHashtags(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate hashtags');
    } finally {
      setLoading(false);
    }
  };

  // Generate Blog Article
  const generateBlog = async () => {
    if (!blogTopic) return;
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/seo/generate-blog`, {
        topic: blogTopic,
        keywords: blogKeywords,
        user_id: user.id,
        industry: user.industry
      });
      setGeneratedBlog(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate blog');
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('✅ Copied to clipboard!');
  };

  // SEO Score Color
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500/20 border-green-500/30';
    if (score >= 60) return 'bg-yellow-500/20 border-yellow-500/30';
    if (score >= 40) return 'bg-orange-500/20 border-orange-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          🔍 AI SEO Suite
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Optimize your website and content for search engines with AI-powered tools
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 p-1 rounded-xl" style={{ backgroundColor: 'var(--secondary-bg)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab.id ? 'accent-gradient text-white shadow-lg' : ''
            }`}
            style={activeTab !== tab.id ? { color: 'var(--text-muted)' } : {}}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400">
          ❌ {error}
        </div>
      )}

      {/* Website Analyzer Tab */}
      {activeTab === 'website' && (
        <div className="space-y-6">
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              🌐 Website SEO Analyzer
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
              Enter any URL to get a comprehensive SEO audit report
            </p>
            
            <div className="flex gap-4 mb-6">
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://example.com"
                className="flex-1 px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
              <button
                onClick={analyzeWebsite}
                disabled={loading || !websiteUrl}
                className="px-6 py-3 accent-gradient text-white font-semibold rounded-xl disabled:opacity-50 hover:opacity-90 transition"
              >
                {loading ? '🔄 Analyzing...' : '🔍 Analyze'}
              </button>
            </div>

            {/* SEO Report */}
            {seoReport && (
              <div className="space-y-4">
                {/* Overall Score */}
                <div className={`p-6 rounded-xl border ${getScoreBg(seoReport.overall_score)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Overall SEO Score</h3>
                      <p style={{ color: 'var(--text-muted)' }}>{seoReport.summary}</p>
                    </div>
                    <div className={`text-5xl font-bold ${getScoreColor(seoReport.overall_score)}`}>
                      {seoReport.overall_score}/100
                    </div>
                  </div>
                </div>

                {/* Raw Data - What We Actually Found */}
                {seoReport.raw_data && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--accent-primary)' }}>
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                      📊 What We Found on Your Website
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {/* Title Tag */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                        <p className="font-medium text-purple-400">Title Tag</p>
                        {seoReport.raw_data.title?.content ? (
                          <>
                            <p style={{ color: 'var(--text-primary)' }}>"{seoReport.raw_data.title.content}"</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                              Length: {seoReport.raw_data.title.length} chars {seoReport.raw_data.title.length >= 50 && seoReport.raw_data.title.length <= 60 ? '✅' : '⚠️'}
                            </p>
                          </>
                        ) : (
                          <p className="text-red-400">❌ Not found</p>
                        )}
                      </div>
                      
                      {/* Meta Description */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                        <p className="font-medium text-purple-400">Meta Description</p>
                        {seoReport.raw_data.meta_description?.content ? (
                          <>
                            <p className="truncate" style={{ color: 'var(--text-primary)' }}>"{seoReport.raw_data.meta_description.content}"</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                              Length: {seoReport.raw_data.meta_description.length} chars {seoReport.raw_data.meta_description.length >= 150 && seoReport.raw_data.meta_description.length <= 160 ? '✅' : '⚠️'}
                            </p>
                          </>
                        ) : (
                          <p className="text-red-400">❌ Not found</p>
                        )}
                      </div>
                      
                      {/* Headings */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                        <p className="font-medium text-purple-400">Heading Structure</p>
                        <p style={{ color: 'var(--text-primary)' }}>
                          H1: {seoReport.raw_data.headings_summary?.h1 || 0} {seoReport.raw_data.headings_summary?.h1 === 1 ? '✅' : '⚠️'} | 
                          H2: {seoReport.raw_data.headings_summary?.h2 || 0} | 
                          H3: {seoReport.raw_data.headings_summary?.h3 || 0}
                        </p>
                      </div>
                      
                      {/* Images */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                        <p className="font-medium text-purple-400">Images</p>
                        <p style={{ color: 'var(--text-primary)' }}>
                          Total: {seoReport.raw_data.images?.total || 0} | 
                          With Alt: {seoReport.raw_data.images?.with_alt || 0} | 
                          Missing Alt: {seoReport.raw_data.images?.without_alt || 0}
                          {seoReport.raw_data.images?.without_alt === 0 ? ' ✅' : ' ⚠️'}
                        </p>
                      </div>
                      
                      {/* Links */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                        <p className="font-medium text-purple-400">Links</p>
                        <p style={{ color: 'var(--text-primary)' }}>
                          Internal: {seoReport.raw_data.links?.internal || 0} | 
                          External: {seoReport.raw_data.links?.external || 0}
                        </p>
                      </div>
                      
                      {/* Technical */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                        <p className="font-medium text-purple-400">Technical</p>
                        <p style={{ color: 'var(--text-primary)' }}>
                          Viewport: {seoReport.raw_data.has_viewport ? '✅' : '❌'} | 
                          OG Tags: {seoReport.raw_data.has_og_tags ? '✅' : '❌'} | 
                          Schema: {seoReport.raw_data.has_structured_data ? '✅' : '❌'}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                          Load time: {seoReport.raw_data.load_time?.toFixed(1)}s
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Score Breakdown */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {seoReport.scores && Object.entries(seoReport.scores).map(([key, value]) => (
                    <div key={key} className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                      <p className="text-sm capitalize" style={{ color: 'var(--text-muted)' }}>{key.replace(/_/g, ' ')}</p>
                      <p className={`text-2xl font-bold ${getScoreColor(value)}`}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Issues */}
                {seoReport.issues && seoReport.issues.length > 0 && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>⚠️ Issues Found</h4>
                    <ul className="space-y-2">
                      {seoReport.issues.map((issue, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                          <span className="text-yellow-500">•</span>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {seoReport.recommendations && seoReport.recommendations.length > 0 && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>✅ Recommendations</h4>
                    <ul className="space-y-2">
                      {seoReport.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                          <span className="text-green-500">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Implementation Guide */}
                <div className="p-4 rounded-xl border mt-4" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--accent-primary)' }}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    🛠️ How to Apply SEO Fixes
                  </h4>
                  <div className="space-y-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <p className="font-medium text-purple-400 mb-1">Step 1: Use Meta Tag Generator</p>
                      <p>Go to the "Meta Generator" tab above and generate optimized meta tags for your pages.</p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <p className="font-medium text-purple-400 mb-1">Step 2: Copy Generated HTML</p>
                      <p>Copy the generated meta tags and Open Graph tags using the copy buttons.</p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <p className="font-medium text-purple-400 mb-1">Step 3: Add to Your Website</p>
                      <p>Paste the meta tags in your website's &lt;head&gt; section. If using WordPress, use Yoast SEO or similar plugin.</p>
                    </div>
                    <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)' }}>
                      <p className="font-medium text-purple-400 mb-1">Step 4: Re-analyze</p>
                      <p>After making changes, re-analyze your website to verify improvements.</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => setActiveTab('meta')}
                      className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                      style={{ background: 'var(--accent-gradient)' }}
                    >
                      🏷️ Generate Meta Tags
                    </button>
                    <button
                      onClick={() => {
                        const report = `
SEO ANALYSIS REPORT
==================
URL: ${websiteUrl}
Overall Score: ${seoReport.overall_score}/100

ISSUES FOUND:
${seoReport.issues?.map(i => `- ${i}`).join('\n') || 'None'}

RECOMMENDATIONS:
${seoReport.recommendations?.map(r => `- ${r}`).join('\n') || 'None'}

Generated by MaxeDigital AI SEO Suite
                        `.trim();
                        navigator.clipboard.writeText(report);
                        alert('Report copied to clipboard!');
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium border"
                      style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)' }}
                    >
                      📋 Copy Full Report
                    </button>
                    <button
                      onClick={() => setActiveTab('content')}
                      className="px-4 py-2 rounded-lg text-sm font-medium border"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                    >
                      📝 Optimize Content
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Meta Generator Tab */}
      {activeTab === 'meta' && (
        <div className="space-y-6">
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              🏷️ Meta Tag Generator
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
              Generate SEO-optimized meta tags for your pages
            </p>

            <div className="grid gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Page Title / Topic *</label>
                <input
                  type="text"
                  value={metaInput.pageTitle}
                  onChange={(e) => setMetaInput({...metaInput, pageTitle: e.target.value})}
                  placeholder="e.g., Best Digital Marketing Services"
                  className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Page Description</label>
                <textarea
                  value={metaInput.pageDescription}
                  onChange={(e) => setMetaInput({...metaInput, pageDescription: e.target.value})}
                  placeholder="Brief description of your page content..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Target Keywords</label>
                  <input
                    type="text"
                    value={metaInput.keywords}
                    onChange={(e) => setMetaInput({...metaInput, keywords: e.target.value})}
                    placeholder="marketing, SEO, digital"
                    className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Target Audience</label>
                  <input
                    type="text"
                    value={metaInput.targetAudience}
                    onChange={(e) => setMetaInput({...metaInput, targetAudience: e.target.value})}
                    placeholder="Small business owners"
                    className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={generateMeta}
              disabled={loading || !metaInput.pageTitle}
              className="px-6 py-3 accent-gradient text-white font-semibold rounded-xl disabled:opacity-50 hover:opacity-90 transition"
            >
              {loading ? '🔄 Generating...' : '✨ Generate Meta Tags'}
            </button>

            {/* Generated Meta */}
            {generatedMeta && (
              <div className="mt-6 space-y-4">
                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Meta Title</h4>
                    <button onClick={() => copyToClipboard(generatedMeta.title)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                  </div>
                  <p className="text-blue-400">{generatedMeta.title}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{generatedMeta.title?.length || 0}/60 characters</p>
                </div>

                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Meta Description</h4>
                    <button onClick={() => copyToClipboard(generatedMeta.description)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                  </div>
                  <p style={{ color: 'var(--text-muted)' }}>{generatedMeta.description}</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{generatedMeta.description?.length || 0}/160 characters</p>
                </div>

                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Keywords</h4>
                    <button onClick={() => copyToClipboard(generatedMeta.keywords)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                  </div>
                  <p style={{ color: 'var(--text-muted)' }}>{generatedMeta.keywords}</p>
                </div>

                {generatedMeta.og_tags && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Open Graph Tags (HTML)</h4>
                      <button onClick={() => copyToClipboard(generatedMeta.og_tags)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                    </div>
                    <pre className="text-xs overflow-x-auto p-2 rounded bg-black/30" style={{ color: 'var(--text-muted)' }}>{generatedMeta.og_tags}</pre>
                  </div>
                )}

                {/* Copy All HTML Button */}
                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--accent-primary)' }}>
                  <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>📄 Complete HTML Code</h4>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                    Copy all meta tags and paste them in your website's &lt;head&gt; section
                  </p>
                  <pre className="text-xs overflow-x-auto p-3 rounded bg-black/50 mb-3" style={{ color: '#10b981' }}>
{`<!-- SEO Meta Tags - Generated by MaxeDigital AI -->
<title>${generatedMeta.title || ''}</title>
<meta name="description" content="${generatedMeta.description || ''}">
<meta name="keywords" content="${generatedMeta.keywords || ''}">
${generatedMeta.og_tags || ''}`}
                  </pre>
                  <button
                    onClick={() => {
                      const fullHtml = `<!-- SEO Meta Tags - Generated by MaxeDigital AI -->
<title>${generatedMeta.title || ''}</title>
<meta name="description" content="${generatedMeta.description || ''}">
<meta name="keywords" content="${generatedMeta.keywords || ''}">
${generatedMeta.og_tags || ''}`;
                      copyToClipboard(fullHtml);
                    }}
                    className="w-full px-4 py-3 rounded-lg text-white font-medium"
                    style={{ background: 'var(--accent-gradient)' }}
                  >
                    📋 Copy All HTML Code
                  </button>
                </div>

                {/* Implementation Instructions */}
                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
                  <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>💡 How to Apply</h4>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <li><strong>WordPress:</strong> Use Yoast SEO plugin or paste in theme's header.php</li>
                    <li><strong>Wix/Squarespace:</strong> Go to Settings → SEO and paste meta info</li>
                    <li><strong>Custom HTML:</strong> Paste inside &lt;head&gt;...&lt;/head&gt; tags</li>
                    <li><strong>React/Next.js:</strong> Use react-helmet or next/head component</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content SEO Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              📝 Content SEO Analyzer
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
              Analyze your content for SEO optimization and get improvement suggestions
            </p>

            <textarea
              value={contentText}
              onChange={(e) => setContentText(e.target.value)}
              placeholder="Paste your content here (social media post, article, product description)..."
              rows={8}
              className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500 mb-4"
              style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />

            <button
              onClick={analyzeContent}
              disabled={loading || !contentText}
              className="px-6 py-3 accent-gradient text-white font-semibold rounded-xl disabled:opacity-50 hover:opacity-90 transition"
            >
              {loading ? '🔄 Analyzing...' : '📊 Analyze Content'}
            </button>

            {/* Content Analysis */}
            {contentAnalysis && (
              <div className="mt-6 space-y-4">
                <div className={`p-6 rounded-xl border ${getScoreBg(contentAnalysis.seo_score)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Content SEO Score</h3>
                      <p style={{ color: 'var(--text-muted)' }}>{contentAnalysis.summary}</p>
                    </div>
                    <div className={`text-5xl font-bold ${getScoreColor(contentAnalysis.seo_score)}`}>
                      {contentAnalysis.seo_score}/100
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>📊 Stats</h4>
                    <ul className="space-y-1 text-sm" style={{ color: 'var(--text-muted)' }}>
                      <li>Word Count: {contentAnalysis.word_count}</li>
                      <li>Reading Time: {contentAnalysis.reading_time}</li>
                      <li>Readability: {contentAnalysis.readability}</li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>🎯 Keywords Found</h4>
                    <div className="flex flex-wrap gap-2">
                      {contentAnalysis.keywords?.map((kw, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">{kw}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {contentAnalysis.suggestions && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>💡 Improvement Suggestions</h4>
                    <ul className="space-y-2">
                      {contentAnalysis.suggestions.map((sug, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                          <span className="text-purple-400">→</span>
                          {sug}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {contentAnalysis.optimized_content && (
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>✨ Optimized Version</h4>
                      <button onClick={() => copyToClipboard(contentAnalysis.optimized_content)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                    </div>
                    <p className="whitespace-pre-wrap" style={{ color: 'var(--text-muted)' }}>{contentAnalysis.optimized_content}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hashtag Generator Tab */}
      {activeTab === 'hashtags' && (
        <div className="space-y-6">
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              #️⃣ Smart Hashtag Generator
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
              Generate trending and relevant hashtags for your {user.industry} content
            </p>

            <div className="flex gap-4 mb-6">
              <input
                type="text"
                value={hashtagTopic}
                onChange={(e) => setHashtagTopic(e.target.value)}
                placeholder="Enter topic or post description..."
                className="flex-1 px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              />
              <button
                onClick={generateHashtags}
                disabled={loading || !hashtagTopic}
                className="px-6 py-3 accent-gradient text-white font-semibold rounded-xl disabled:opacity-50 hover:opacity-90 transition"
              >
                {loading ? '🔄 Generating...' : '#️⃣ Generate'}
              </button>
            </div>

            {/* Generated Hashtags */}
            {generatedHashtags && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>🔥 Trending Hashtags</h4>
                    <button onClick={() => copyToClipboard(generatedHashtags.trending?.join(' '))} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy All</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {generatedHashtags.trending?.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full cursor-pointer hover:bg-orange-500/30" onClick={() => copyToClipboard(tag)}>{tag}</span>
                    ))}
                  </div>
                </div>

                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>🎯 Niche Hashtags</h4>
                    <button onClick={() => copyToClipboard(generatedHashtags.niche?.join(' '))} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy All</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {generatedHashtags.niche?.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full cursor-pointer hover:bg-purple-500/30" onClick={() => copyToClipboard(tag)}>{tag}</span>
                    ))}
                  </div>
                </div>

                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>📈 Growth Hashtags</h4>
                    <button onClick={() => copyToClipboard(generatedHashtags.growth?.join(' '))} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy All</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {generatedHashtags.growth?.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full cursor-pointer hover:bg-green-500/30" onClick={() => copyToClipboard(tag)}>{tag}</span>
                    ))}
                  </div>
                </div>

                <div className="p-4 rounded-xl border accent-gradient">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-white">✨ Recommended Combo</h4>
                    <button onClick={() => copyToClipboard(generatedHashtags.recommended_combo)} className="text-sm text-white/80 hover:text-white">📋 Copy</button>
                  </div>
                  <p className="text-white/90">{generatedHashtags.recommended_combo}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blog Writer Tab */}
      {activeTab === 'blog' && (
        <div className="space-y-6">
          <div className="rounded-2xl p-6 border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              📰 AI Blog Writer
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
              Generate SEO-optimized blog articles with proper structure
            </p>

            <div className="grid gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Blog Topic *</label>
                <input
                  type="text"
                  value={blogTopic}
                  onChange={(e) => setBlogTopic(e.target.value)}
                  placeholder="e.g., 10 Digital Marketing Trends for 2025"
                  className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Target Keywords (comma separated)</label>
                <input
                  type="text"
                  value={blogKeywords}
                  onChange={(e) => setBlogKeywords(e.target.value)}
                  placeholder="digital marketing, SEO, social media"
                  className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-purple-500"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>

            <button
              onClick={generateBlog}
              disabled={loading || !blogTopic}
              className="px-6 py-3 accent-gradient text-white font-semibold rounded-xl disabled:opacity-50 hover:opacity-90 transition"
            >
              {loading ? '🔄 Writing Article...' : '✍️ Generate Blog Article'}
            </button>

            {/* Generated Blog */}
            {generatedBlog && (
              <div className="mt-6 space-y-4">
                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>📌 SEO Title</h4>
                    <button onClick={() => copyToClipboard(generatedBlog.title)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                  </div>
                  <p className="text-lg font-semibold text-blue-400">{generatedBlog.title}</p>
                </div>

                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>📝 Meta Description</h4>
                    <button onClick={() => copyToClipboard(generatedBlog.meta_description)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy</button>
                  </div>
                  <p style={{ color: 'var(--text-muted)' }}>{generatedBlog.meta_description}</p>
                </div>

                <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>📄 Full Article</h4>
                    <button onClick={() => copyToClipboard(generatedBlog.content)} className="text-sm text-purple-400 hover:text-purple-300">📋 Copy All</button>
                  </div>
                  <div className="prose prose-invert max-w-none" style={{ color: 'var(--text-muted)' }}>
                    <div dangerouslySetInnerHTML={{ __html: generatedBlog.content_html || generatedBlog.content?.replace(/\n/g, '<br/>') }} />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>📊 Article Stats</h4>
                    <ul className="space-y-1 text-sm" style={{ color: 'var(--text-muted)' }}>
                      <li>Word Count: {generatedBlog.word_count}</li>
                      <li>Reading Time: {generatedBlog.reading_time}</li>
                      <li>SEO Score: <span className={getScoreColor(generatedBlog.seo_score)}>{generatedBlog.seo_score}/100</span></li>
                    </ul>
                  </div>
                  <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--secondary-bg)', borderColor: 'var(--border-color)' }}>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>🏷️ Suggested Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {generatedBlog.tags?.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AISEOSection;
